export type Route = JoinedKeyof<RecursiveOmit<typeof ROUTES, 'root' | 'alias'>>;
type Routes = Partial<
  Record<Exclude<RecursiveKeyof<typeof ROUTES>, 'root' | 'alias'>, string>
>;

type APIRoute = JoinedKeyof<RecursiveOmit<typeof API_ROUTES, 'root'>>;
type APIRoutes = Partial<
  Record<Exclude<RecursiveKeyof<typeof API_ROUTES>, 'root'>, string>
>;

/**
 * The routes schema.
 *
 * Routes can be nested by providing an object containing an optional `root` key.
 * Routes can be dynamic by defining them as a glob pattern. (only single asterisk is supported)
 */
const ROUTES = {
  base: {
    root: '/',
    alias: 'صفحه اصلی',

    about: {alias: 'درباره ما'},
    blog: {
      root: process.env.NEXT_PUBLIC_BLOG_PATH!,
      alias: 'بلاگ'
    },
    chat: {
      root: process.env.NEXT_PUBLIC_CHAT_PATH!,
      alias: 'چت بات'
    },
    contact: {alias: 'تماس با ما'},
    stacking: {alias: 'استیکینگ'},
    faq: {alias: 'سوالات متداول'},
    invitation: {alias: 'دعوت دوستان'},
    markets: {
      alias: 'بازارها',
      coin: '*'
    },
    otc: {alias: 'سفارشات سریع'},
    support: {alias: 'پشتیبانی'},
    terms: {alias: 'شرایط و مقررات'},
    trade: {
      root: process.env.NEXT_PUBLIC_TRADE_PATH!,
      alias: 'اسپات',
      coin: '*'
    },
    user: {
      root: process.env.NEXT_PUBLIC_USER_PATH!,
      dashboard: {alias: 'داشبورد'},
      security: {alias: 'امنیت'},
      invite: 'invite',
      notification: 'notification',
      wallets: {
        alias: 'کیف پول',
        depositFiat: 'depositFiat',
        withdrawFiat: 'withdrawFiat'
      }
    },
    login: {alias: 'ورود'},
    register: {alias: 'ثبت نام'},
    sitemap: 'sitemap.xml',
    security: {alias: 'امنیت'},
    signal: {
      alias: 'آنالیز سنتر',
      coin: '*'
    },
    'user-levels': {alias: 'سطوح کاربری'},
    'fee-levels': {alias: 'کارمزدها'},
    'free-transfer': {alias: 'انتقال داخلی بدون کارمزد'},
    'identity-verification': {
      alias: 'احراز هویت',
      gift: {alias: 'هدیه'},
      basic: {alias: 'اطلاعات پایه'},
      document: {alias: 'مدرک شناسایی'},
      recording: {alias: 'معرفی تصویری'},
      result: {alias: 'نتیجه'}
    }
  }
};

/**
 * The API endpoints route schema.
 *
 * Routes can be nested by providing an object containing an optional `root` key.
 * Routes can be dynamic by defining them as a glob pattern. (only single asterisk is supported)
 */
const API_ROUTES = {
  base: {
    root: `/${process.env.NEXT_PUBLIC_API_PATH!}`,

    otc: 'otc',
    contents: {
      blog: 'blog',
      coin: '*'
    },
    prices: {
      market: 'market',
      losers: 'losers',
      gainers: 'gainers',
      trends: 'trends'
    },
    profile: {
      'identity-verify': 'identity-verify',
      idCard: 'idCard',
      selfi: 'selfi',
      otp: {
        verify: 'verify'
      },
      kyc: {
        claim: 'claim'
      }
    },
    public: {
      stars: 'stars',
      'trade-fee': 'trade-fee',
      'transfer-fee': 'transfer-fee'
    },
    auth: {
      login: 'login',
      register: 'register',
      sessions: {
        token: '*'
      },
      otp: {
        verify: 'verify',
        register: {
          verify: 'verify'
        },
        'forget-password': {
          verify: 'verify'
        }
      },
      '2fa': {
        verify: 'verify'
      }
    },
    users: {
      'set-password': 'set-password'
    },
    crypto: {
      tokens: {
        all: {
          public: 'public'
        }
      }
    },
    notifications: {
      unread: 'unread',
      id: '*',
      read: {
        all: 'all',
        id: '*'
      }
    },
    settings: {
      coins: 'coins',
      banks: 'banks',
      items: {
        block: 'block'
      },
      wages: {
        calculate: 'calculate'
      },
      'customer-loyalty-program': {
        'kyc-gift': 'kyc-gift'
      }
    },
    signal: {
      coins: 'coins',
      id: '*'
    },
    'coin-category': {
      id: '*'
    },
    'customer-loyalty-program': {
      'kyc-gift': 'kyc-gift'
    }
  },
  static: {
    root: '/',
    assets: {
      avatars: {
        id: '*'
      },
      contents: {
        id: '*'
      }
    },
    graphs: {
      coin: '*.svg'
    },
    signals: {
      name: '*'
    }
  }
};

/**
 * Resolves the given route to corresponding path based on the routes schema.
 *
 * @param replacements - The custom route segments to replace the
 * dynamic routes (glob pattern) when building the path.
 * @param asAlias - Return the route alias instead if defined.
 */
function resolve(
  routes: typeof ROUTES | typeof API_ROUTES,
  route: Route | APIRoute,
  replacements?: Routes | APIRoutes,
  asAlias?: boolean
) {
  const path: string[] = [];
  const routesSplitted = route.split('.');
  let currentRoute: any = routes;
  let currentAlias = '';

  for (let index = 0; index < routesSplitted.length; index++) {
    const subRoute = routesSplitted[index];
    currentRoute = currentRoute[subRoute];
    const isNested = typeof currentRoute !== 'string';

    if (currentRoute === undefined) {
      break;
    }

    if (
      isNested &&
      'alias' in currentRoute &&
      index + 1 === routesSplitted.length
    )
      currentAlias = currentRoute.alias;

    if (replacements) {
      const replacementRoute = (replacements as Routes)[
        subRoute as keyof Routes
      ];
      if (replacementRoute) {
        path.push(
          currentRoute.includes('*')
            ? currentRoute.replace(/\*/, replacementRoute)
            : replacementRoute
        );
        continue;
      }
    }

    path.push(
      isNested && 'root' in currentRoute
        ? currentRoute.root
        : isNested
        ? subRoute
        : currentRoute
    );
  }

  return asAlias ? currentAlias : path.join('/').replaceAll('//', '/');
}

/**
 * Resolves the given route to corresponding path based on the routes schema.
 *
 * @param replacements - The custom route segments to replace the
 * dynamic routes (glob pattern) when building the path.
 * @param asAlias - Return the route alias instead if defined.
 */
export function resolveRoute(
  route: Route,
  replacements?: Routes,
  asAlias?: boolean
) {
  return resolve(ROUTES, route, replacements, asAlias);
}

/**
 * Resolves the given route to corresponding path based on the API endpoints routes schema.
 * @param replacements - The custom route segments to replace the
 * dynamic routes (glob pattern) when building the path.
 * @param queryParameters - The query parameters specified by key value pairs.
 */
export function resolveAPIRoute(
  route: APIRoute,
  replacements?: APIRoutes,
  queryParameters: {[key: string]: any} = {}
) {
  const url = new URL(
    `${process.env.NEXT_PUBLIC_PROTOCOL}://${
      process.env.NEXT_PUBLIC_API_DOMAIN
    }${resolve(API_ROUTES, route, replacements)}`
  );

  if (queryParameters) {
    url.search = new URLSearchParams(queryParameters).toString();
  }

  return url;
}
